import { useAuth } from '../../context/AuthContext';
import {
  BarChart3,
  Package,
  Layers,
  ShoppingBag,
  Settings,
  Shield,
  LogOut,
  ExternalLink,
} from 'lucide-react';

interface AdminLayoutProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  children: React.ReactNode;
}

export function AdminLayout({ currentPath, onNavigate, children }: AdminLayoutProps) {
  const { user, logout } = useAuth();

  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center space-y-4">
        <div className="w-12 h-12 rounded-full bg-red-100 text-red-700 mx-auto flex items-center justify-center">
          <Shield className="w-6 h-6" />
        </div>
        <h2 className="text-xl font-serif font-medium text-ink">Acesso Restrito ao Administrador</h2>
        <p className="text-xs text-ink-muted leading-relaxed">
          Esta área requer credenciais administrativas com autenticação multifator ativa.
        </p>
        <button
          onClick={() => onNavigate('/login?returnTo=/admin')}
          className="bg-ink text-ink-inverted px-6 py-2.5 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors"
        >
          Acessar com Conta de Administrador
        </button>
      </div>
    );
  }

  const navItems = [
    { label: 'Visão Geral / Vendas', path: '/admin', icon: BarChart3 },
    { label: 'Produtos & Peças', path: '/admin/produtos', icon: Package },
    { label: 'Categorias & Árvore', path: '/admin/categorias', icon: Layers },
    { label: 'Pedidos & Fulfillment', path: '/admin/pedidos', icon: ShoppingBag },
    { label: 'Configurações da Loja', path: '/admin/configuracoes', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      {/* Sidebar Navigation */}
      <aside className="w-full md:w-64 bg-surface border-r border-border-subtle p-6 flex flex-col justify-between shrink-0">
        <div className="space-y-6">
          <div className="flex items-center justify-between border-b border-border-subtle pb-4">
            <div>
              <span className="font-serif text-lg font-medium tracking-tight text-ink block">
                Aura Ateliê
              </span>
              <span className="text-[10px] uppercase tracking-widest font-mono text-primary font-semibold">
                Painel Admin D1
              </span>
            </div>
          </div>

          <nav className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPath === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => onNavigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xs text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-ink text-ink-inverted font-semibold'
                      : 'text-ink-muted hover:text-ink hover:bg-background-alt'
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="pt-6 border-t border-border-subtle space-y-2 text-xs">
          <button
            onClick={() => onNavigate('/')}
            className="w-full flex items-center justify-between p-2 text-ink-muted hover:text-ink transition-colors rounded-xs"
          >
            <span className="flex items-center gap-2">
              <ExternalLink className="w-3.5 h-3.5" /> Ver Loja Pública
            </span>
            &rarr;
          </button>

          <button
            onClick={logout}
            className="w-full flex items-center gap-2 p-2 text-red-700 hover:bg-red-50 transition-colors rounded-xs"
          >
            <LogOut className="w-3.5 h-3.5" /> Sair do Painel
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 p-6 md:p-10 max-w-7xl overflow-y-auto">
        {children}
      </main>
    </div>
  );
}
